import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import Landing from './components/Landing';
import Pricing from './components/Pricing';
import VowsForm from './components/VowsForm';
import VowsDisplay from './components/VowsDisplay';
import Redeem from './components/Redeem';
import Help from './components/Help';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import About from './components/About';
import PaymentSuccess from './components/PaymentSuccess';
import ExitIntentPopup from './components/ExitIntentPopup';
import Blog from './components/Blog';
import BlogPost from './components/BlogPost';
import LeadMagnet from './components/LeadMagnet';
import HeartsBackground from './components/HeartsBackground';
import SeoPage, { SEO_PATHS } from './components/SeoPage';
import Admin from './components/Admin';

function App() {
  const [currentStep, setCurrentStep] = useState('landing');
  const [vowsData, setVowsData] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    const path = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    const shouldShowCheckout = urlParams.get('checkout') === 'true';
    const paymentSuccess = urlParams.get('payment') === 'success';
    const paymentCancelled = urlParams.get('payment') === 'cancelled';
    
    if (paymentSuccess) {
      setCurrentStep('payment-success');
      window.history.replaceState({ step: 'payment-success' }, '', '/');
    } else if (paymentCancelled) {
      setCurrentStep('pricing');
      window.history.replaceState({ step: 'pricing' }, '', '/');
    } else if (path === '/redeem') {
      setCurrentStep('redeem');
      window.history.replaceState({ step: 'redeem' }, '', '/redeem');
    } else if (path === '/help') {
      setCurrentStep('help');
      window.history.replaceState({ step: 'help' }, '', '/help');
    } else if (path === '/terms') {
      setCurrentStep('terms');
      window.history.replaceState({ step: 'terms' }, '', '/terms');
    } else if (path === '/privacy') {
      setCurrentStep('privacy');
      window.history.replaceState({ step: 'privacy' }, '', '/privacy');
    } else if (path === '/about') {
      setCurrentStep('about');
      window.history.replaceState({ step: 'about' }, '', '/about');
    } else if (path.startsWith('/blog/')) {
      setCurrentStep('blog-post');
    } else if (path === '/blog') {
      setCurrentStep('blog');
      window.history.replaceState({ step: 'blog' }, '', '/blog');
    } else if (path === '/free-vow-prompts') {
      setCurrentStep('lead-magnet');
      window.history.replaceState({ step: 'lead-magnet' }, '', '/free-vow-prompts');
    } else if (path === '/admin') {
      setCurrentStep('admin');
      window.history.replaceState({ step: 'admin' }, '', '/admin');
    } else if (SEO_PATHS.includes(path)) {
      setCurrentStep('seo-page');
      window.history.replaceState({ step: 'seo-page' }, '', path);
    } else if (shouldShowCheckout) {
      setCurrentStep('pricing');
      window.history.replaceState({ step: 'pricing' }, '', '/');
    } else {
      setCurrentStep('landing');
      window.history.replaceState({ step: 'landing' }, '', '/');
    }
    
    const savedScrollPosition = sessionStorage.getItem('scrollPosition');
    if (savedScrollPosition) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScrollPosition, 10));
        sessionStorage.removeItem('scrollPosition');
      }, 100);
    }
  }, []);

  useEffect(() => {
    if (!window.history.state || !window.history.state.step) {
      window.history.replaceState({ step: 'landing' }, '', '/');
    }

    const handlePopState = (event) => {
      if (event.state && event.state.step) {
        setCurrentStep(event.state.step);
      } else {
        setCurrentStep('landing');
        window.history.pushState({ step: 'landing' }, '', '/');
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleGetStarted = () => {
    setCurrentStep('pricing');
    window.history.pushState({ step: 'pricing' }, '', '/');
    setTimeout(() => {
      const element = document.getElementById('pricing');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handlePaymentSuccess = (plan) => {
    setSelectedPlan(plan);
    setCurrentStep('form');
  };

  const handleGenerateVows = (data) => {
    setVowsData(data);
    setCurrentStep('display');
  };

  const handleNewVows = () => {
    setVowsData(null);
    setCurrentStep('landing');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="App">
      {/* Hearts Background Effect */}
      <HeartsBackground />
      
      {/* Page Corner Decorations */}
      <div className="page-decoration decoration-top-left"></div>
      <div className="page-decoration decoration-top-right"></div>
      <div className="page-decoration decoration-bottom-left"></div>
      <div className="page-decoration decoration-bottom-right"></div>
      
      <Header onGetStarted={handleGetStarted} />
      
      {currentStep === 'landing' && (
        <Landing onGetStarted={handleGetStarted} />
      )}

      {currentStep === 'pricing' && (
        <Pricing 
          onPaymentSuccess={handlePaymentSuccess}
          autoShowPayment={new URLSearchParams(window.location.search).get('checkout') === 'true'}
        />
      )}

      {currentStep === 'form' && (
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-8">
              <div className="glass-card mb-4 text-center">
                <h1 className="commercial-heading mb-3">Create Your Wedding Vows</h1>
                <p className="commercial-subheading mb-0">
                  Share your love story and we'll craft beautiful, heartfelt vows
                </p>
                {selectedPlan && (
                  <div className="mt-3">
                    <span className="badge" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #1a365d 100%)', color: 'white' }}>Package: {selectedPlan.name}</span>
                  </div>
                )}
              </div>
              <VowsForm onGenerate={handleGenerateVows} />
            </div>
          </div>
        </div>
      )}

      {currentStep === 'display' && (
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-8">
              <VowsDisplay 
                data={vowsData} 
                onNewVows={handleNewVows}
              />
            </div>
          </div>
        </div>
      )}

      {currentStep === 'redeem' && (
        <Redeem />
      )}

      {currentStep === 'help' && (
        <Help />
      )}

      {currentStep === 'terms' && (
        <Terms />
      )}

      {currentStep === 'privacy' && (
        <Privacy />
      )}

      {currentStep === 'about' && (
        <About />
      )}

      {currentStep === 'payment-success' && (
        <PaymentSuccess />
      )}

      {currentStep === 'blog' && (
        <Blog onNavigateToPost={(slug) => {
          window.history.pushState({ step: 'blog-post' }, '', `/blog/${slug}`);
          setCurrentStep('blog-post');
        }} />
      )}

      {currentStep === 'blog-post' && (
        <BlogPost slug={window.blogPostSlug} />
      )}

      {currentStep === 'lead-magnet' && (
        <LeadMagnet />
      )}

      {currentStep === 'seo-page' && (
        <SeoPage onGetStarted={handleGetStarted} />
      )}

      {currentStep === 'admin' && (
        <Admin />
      )}

      {/* Exit Intent Popup - Only show on landing page */}
      {currentStep === 'landing' && (
        <ExitIntentPopup onGetStarted={handleGetStarted} />
      )}

      <Footer onGetStarted={handleGetStarted} />
    </div>
  );
}

export default App;
